import React, { useState, useEffect } from 'react';
import {
    Card,
    Typography,
    Row,
    Col,
    Button,
    Select,
    Form,
    message,
    Space,
    Pagination,
    Empty,
    Image,
    Spin,
    Modal
} from 'antd';
import { PictureOutlined, TagOutlined, ReloadOutlined } from '@ant-design/icons';
import { ApiService } from '../services/api';
import { Image as ImageType, Tag, UntaggedImagesRequest } from '../types';

const { Title, Text } = Typography;
const { Option } = Select;

const ImageTagging: React.FC = () => {
    const [form] = Form.useForm();
    const [images, setImages] = useState<ImageType[]>([]);
    const [tags, setTags] = useState<Tag[]>([]);
    const [loading, setLoading] = useState(false);
    const [tagging, setTagging] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 12,
        total: 0,
        totalPages: 0
    });
    const [selectedImage, setSelectedImage] = useState<ImageType | null>(null);
    const [selectedTag, setSelectedTag] = useState<number | undefined>();
    const [imageHost, setImageHost] = useState<string>('');

    useEffect(() => {
        loadTags();
        loadImages();
    }, []);

    const loadTags = async () => {
        try {
            const response = await ApiService.getTagList({ language: 'zh' });
            if (response.code === 200) {
                setTags(response.data.tags);
            }
        } catch (error: any) {
            message.error('获取标签列表失败');
        }
    };

    const loadImages = async (page: number = 1) => {
        setLoading(true);
        try {
            const request: UntaggedImagesRequest = {
                language: 'zh',
                page,
                page_size: pagination.pageSize,
                is_animated: false
            };

            const response = await ApiService.getUntaggedImages(request);
            if (response.code === 200) {
                setImages(response.data.images);
                setImageHost(response.data.image_host);
                setPagination({
                    page: response.data.pagination.page,
                    pageSize: response.data.pagination.page_size,
                    total: response.data.pagination.total,
                    totalPages: response.data.pagination.total_pages
                });
            } else {
                message.error(response.message || '获取图片列表失败');
            }
        } catch (error: any) {
            message.error(error.response?.data?.message || '获取图片列表失败');
        } finally {
            setLoading(false);
        }
    };

    const handleAddTag = async () => {
        if (!selectedImage || !selectedTag) {
            message.error('请选择图片和标签');
            return;
        }

        setTagging(true);
        try {
            const response = await ApiService.addImageTag({
                image_id: selectedImage.id,
                tag_id: selectedTag
            });

            if (response.code === 200) {
                message.success('标签添加成功');
                // 从列表中移除已标记的图片
                setImages(prev => prev.filter(img => img.id !== selectedImage.id));
                setSelectedImage(null);
                setSelectedTag(undefined);
                form.resetFields();

                // 如果当前页没有图片了，重新加载
                if (images.length === 1 && pagination.page > 1) {
                    loadImages(pagination.page - 1);
                } else if (images.length === 1 && pagination.page === 1) {
                    loadImages(1);
                }
            } else {
                message.error(response.message || '标签添加失败');
            }
        } catch (error: any) {
            message.error(error.response?.data?.message || '标签添加失败');
        } finally {
            setTagging(false);
        }
    };

    const handlePageChange = (page: number) => {
        loadImages(page);
    };

    const getImageUrl = (image: ImageType) => {
        return `${imageHost}${image.url}`;
    };

    return (
        <div>
            <Title level={2}>
                <PictureOutlined /> 图片标记
            </Title>

            <Card style={{ marginBottom: 24 }}>
                <Row gutter={24} align="middle">
                    <Col span={12}>
                        <Text type="secondary">
                            为无标签的图片添加合适的标签分类，便于后续管理和查找
                        </Text>
                    </Col>
                    <Col span={12} style={{ textAlign: 'right' }}>
                        <Space>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => loadImages(1)}
                                loading={loading}
                            >
                                刷新
                            </Button>
                            <Text type="secondary">
                                共 {pagination.total} 张无标签图片
                            </Text>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Spin spinning={loading}>
                {images.length === 0 ? (
                    <Card>
                        <Empty
                            description="暂无无标签图片"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    </Card>
                ) : (
                    <>
                        <Row gutter={[16, 16]}>
                            {images.map((image) => (
                                <Col xs={12} sm={8} md={6} lg={4} key={image.id}>
                                    <Card
                                        hoverable
                                        cover={
                                            <div style={{ height: 200, overflow: 'hidden' }}>
                                                <Image
                                                    src={getImageUrl(image)}
                                                    alt={`图片 ${image.id}`}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover'
                                                    }}
                                                    preview={false}
                                                />
                                            </div>
                                        }
                                        actions={[
                                            <Button
                                                type="primary"
                                                size="small"
                                                icon={<TagOutlined />}
                                                onClick={() => setSelectedImage(image)}
                                            >
                                                添加标签
                                            </Button>
                                        ]}
                                    >
                                        <Card.Meta
                                            title={`图片 ID: ${image.id}`}
                                            description={
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    点击添加标签进行分类
                                                </Text>
                                            }
                                        />
                                    </Card>
                                </Col>
                            ))}
                        </Row>

                        <div style={{ textAlign: 'center', marginTop: 32 }}>
                            <Pagination
                                current={pagination.page}
                                total={pagination.total}
                                pageSize={pagination.pageSize}
                                onChange={handlePageChange}
                                showSizeChanger={false}
                                showQuickJumper
                                showTotal={(total, range) =>
                                    `第 ${range[0]}-${range[1]} 张，共 ${total} 张图片`
                                }
                            />
                        </div>
                    </>
                )}
            </Spin>

            {/* 添加标签模态框 */}
            <Modal
                title="为图片添加标签"
                open={!!selectedImage}
                onOk={handleAddTag}
                onCancel={() => {
                    setSelectedImage(null);
                    setSelectedTag(undefined);
                    form.resetFields();
                }}
                confirmLoading={tagging}
                okText="添加标签"
                cancelText="取消"
            >
                {selectedImage && (
                    <div>
                        <div style={{ textAlign: 'center', marginBottom: 16 }}>
                            <Image
                                src={getImageUrl(selectedImage)}
                                alt="选中的图片"
                                style={{ maxWidth: '100%', maxHeight: 300 }}
                            />
                        </div>

                        <Form form={form} layout="vertical">
                            <Form.Item
                                label="选择标签"
                                name="tag_id"
                                rules={[{ required: true, message: '请选择标签' }]}
                            >
                                <Select
                                    placeholder="请选择标签"
                                    value={selectedTag}
                                    onChange={setSelectedTag}
                                >
                                    {tags.map(tag => (
                                        <Option key={tag.id} value={tag.id}>
                                            {tag.tag} ({tag.remark})
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Form>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default ImageTagging; 